import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { TourCard } from '../components/TourCard';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { Seo } from '../components/Seo';
import { breadcrumb } from '../lib/structuredData';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { ChevronLeft, Sparkles, TrendingUp, BadgeCheck, Megaphone } from 'lucide-react';

function Lane({ title, icon: Ic, tours, currency, testid }) {
  if (!tours || tours.length === 0) return null;
  return (
    <section className="mb-8" data-testid={testid}>
      <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-3"><Ic className="h-5 w-5 text-accent" /> {title}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{tours.map((t) => <TourCard key={t.tour_id + title} t={t} currency={currency} />)}</div>
    </section>
  );
}

export default function ToursDestination({ slug }) {
  const params = useParams();
  const destSlug = slug || params.slug;
  const { currency } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.toursByDestination(destSlug).then(({ data }) => { setData(data); }).catch(() => setData({ tours: [] }));
    api.toursTrack('tour_impression', { surface: 'tour_destination', destination: destSlug });
  }, [destSlug]);

  if (!data) return <Layout><div className="max-w-7xl mx-auto px-4 py-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}</div></Layout>;

  const d = data.destination || {};
  const cityName = d.city || destSlug;
  const destTitle = `Tours in ${cityName}`;
  const destDesc = `Browse ${data.count || ''} multi-day tour${data.count === 1 ? '' : 's'} in ${cityName}${d.country ? `, ${d.country}` : ''}. Compare guided, group & private tours across partners on FlightDeals.in.`;
  const destJsonLd = breadcrumb([
    { name: 'Home', path: '/' },
    { name: 'Tours', path: '/tours' },
    { name: cityName },
  ]);
  return (
    <Layout>
      <Seo title={destTitle} description={destDesc} path={`/tours/${destSlug}`}
        image={d.image} jsonLd={destJsonLd} />
      <section className="relative bg-primary text-primary-foreground">
        {d.image && <img src={d.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Link to="/tours" className="inline-flex items-center gap-1 text-sm text-primary-foreground/80 hover:text-primary-foreground mb-2"><ChevronLeft className="h-4 w-4" /> All tours</Link>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold capitalize">Tours in {d.city || destSlug}</h1>
          <p className="text-primary-foreground/80 mt-1">{data.count} multi-day tour{data.count === 1 ? '' : 's'} · {d.country}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {data.count === 0 ? (
          <Card className="p-16 text-center rounded-2xl" data-testid="dest-tours-empty"><p className="text-muted-foreground">No tours here yet — check back soon.</p></Card>
        ) : (
          <>
            <div className="mb-6"><AffiliateDisclosure variant="banner" /></div>
            <Lane title="Sponsored tours" icon={Megaphone} tours={data.sponsored} currency={currency} testid="dest-sponsored" />
            <Lane title="Featured tours" icon={Sparkles} tours={data.featured} currency={currency} testid="dest-featured" />
            <Lane title="Best value" icon={BadgeCheck} tours={data.best_value} currency={currency} testid="dest-best-value" />
            <Lane title="Popular" icon={TrendingUp} tours={data.popular} currency={currency} testid="dest-popular" />
            <section data-testid="dest-all">
              <h2 className="font-display text-lg font-semibold mb-3">All tours in {d.city || destSlug}</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{data.tours.map((t) => <TourCard key={t.tour_id} t={t} currency={currency} />)}</div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}
